import { createTheme, type Theme } from '@mui/material/styles';
import { tokens } from './tokens';

export function createAppTheme(darkMode: boolean): Theme {
  const base = darkMode
    ? {
        mode: 'dark' as const,
        primary: {
          light: tokens.colors.primaryLight,
          main: tokens.colors.primary,
          dark: tokens.colors.primaryDark,
          contrastText: '#fff',
        },
        secondary: {
          light: tokens.colors.secondaryLight,
          main: tokens.colors.secondary,
          dark: tokens.colors.secondaryDark,
          contrastText: '#000',
        },
        warning: {
          main: tokens.colors.warning,
          contrastText: '#fff',
        },
        background: {
          default: tokens.colors.backgroundDark,
          paper: tokens.colors.paperDark,
        },
        text: {
          primary: '#fff',
          secondary: 'rgba(255, 255, 255, 0.72)',
          disabled: 'rgba(255, 255, 255, 0.5)',
        },
        divider: 'rgba(255, 255, 255, 0.08)',
      }
    : {
        mode: 'light' as const,
        primary: {
          light: tokens.colors.primaryLight,
          main: tokens.colors.primary,
          dark: tokens.colors.primaryDark,
          contrastText: '#fff',
        },
        secondary: {
          light: tokens.colors.secondaryLight,
          main: tokens.colors.secondary,
          dark: tokens.colors.secondaryDark,
          contrastText: '#000',
        },
        warning: {
          main: tokens.colors.warning,
          contrastText: '#fff',
        },
        background: {
          default: tokens.colors.backgroundLight,
          paper: '#ffffff',
        },
        divider: 'rgba(18, 4, 125, 0.08)',
      };

  return createTheme({
    palette: base,
    shape: {
      borderRadius: tokens.shape.borderRadius,
    },
    typography: {
      fontFamily: [
        'Quicksand',
        'Arial',
        'sans-serif',
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
      ].join(','),
      h5: {
        fontWeight: 600,
        letterSpacing: '0.01em',
      },
      h6: {
        fontWeight: 600,
      },
      overline: {
        fontWeight: 600,
        letterSpacing: '0.08em',
        fontSize: '0.7rem',
      },
      button: {
        fontWeight: 600,
        textTransform: 'none',
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontFamily: 'Quicksand, Arial, sans-serif',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundImage: 'none',
            borderRadius: tokens.shape.borderRadius,
            boxShadow: tokens.shadow.card,
            margin: `${tokens.layout.appBarMargin}px ${tokens.layout.appBarMargin}px 0 0`,
            ...(theme.palette.mode === 'dark'
              ? { backgroundColor: tokens.colors.paperDark }
              : { backgroundColor: theme.palette.background.paper }),
          }),
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: ({ theme }) => ({
            borderRight: `1px solid ${theme.palette.divider}`,
            ...(theme.palette.mode === 'dark'
              ? {
                  backgroundColor: tokens.colors.paperDark,
                  backgroundImage: 'none',
                }
              : {
                  backgroundColor: '#ffffff',
                }),
          }),
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: tokens.shape.borderRadiusSm,
            margin: '2px 8px',
            transition: 'background-color 0.2s ease, color 0.2s ease',
            '&.Mui-selected': {
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(127, 225, 221, 0.14)'
                  : 'rgba(18, 4, 125, 0.08)',
              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(127, 225, 221, 0.2)'
                    : 'rgba(18, 4, 125, 0.12)',
              },
              '& .MuiListItemIcon-root': {
                color: theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.primary.main,
              },
              '& .MuiListItemText-primary': {
                fontWeight: 600,
              },
            },
            '&:hover': {
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.06)'
                  : 'rgba(18, 4, 125, 0.04)',
            },
          }),
        },
      },
      MuiPaper: {
        styleOverrides: {
          rounded: {
            borderRadius: tokens.shape.borderRadius,
          },
          elevation1: {
            boxShadow: tokens.shadow.subtle,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: tokens.shape.borderRadiusSm,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 600,
          },
        },
      },
    },
  });
}
